import React from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import styled from "styled-components";
import { ContentType } from ".";
import Typography from "../../designComponents/Typography";
import { ChevronRight } from "./icons";

const DropDownContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DropDownContent = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? "flex" : "none")};
  flex-direction: column;
  margin-left: 25px;

  @media (max-width: 1024px) {
    margin-left: 0;
    align-items: center;
  }
`;

export const MenuItem = styled.div<{
  isActive: boolean;
  windowIsSmall: boolean;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 10px;
  padding: 10px;
  transition: background-color 0.3s ease;
  white-space: nowrap;
  background-color: ${({ isActive, windowIsSmall }) =>
    isActive && windowIsSmall
      ? "transparent"
      : isActive && !windowIsSmall
      ? "#D9E4F7"
      : null};
  border-radius: 12px;
  &:hover {
    background-color: #f1f1f1;
  }

  @media (max-width: 1024px) {
    justify-content: center;
    padding: 0px;

    & span {
      display: none; /* Hide the text */
    }
  }
`;
const DropDownHeader = styled(MenuItem)`
  justify-content: space-between;
  margin-bottom: 10px;
`;

const DropDownCollapsible = ({
  currentPathname,
  icon,
  label,
  windowWidth,
  dropItems,
}: {
  label: string;
  dropItems: {
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    label: string;
    path: string;
  }[];
  icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & { title?: string }
  >;
  currentPathname: string;
  windowWidth: number;
}) => {
  const [open, setOpen] = React.useState(false);

  const navigate = useNavigate();
  const MenuIcon = icon;

  // Check if any child is active
  const isActiveParent = dropItems.some((i) => i.path === currentPathname);

  return (
    <DropDownContainer>
      <DropDownHeader
        isActive={isActiveParent}
        windowIsSmall={windowWidth < 1024}
        onClick={() => setOpen((prev) => !prev)}
        data-tooltip-id={label}
        data-tooltip-content={label}
      >
        {windowWidth < 1024 && <Tooltip id={label} />}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <MenuIcon
            fill={isActiveParent ? "#0A56AF" : "#7E7E7E"}
            height={16}
            width={16}
          />

          {windowWidth >= 1025 && (
            <Typography
              fontFamily="urbanist"
              fontWeight="medium"
              style={{
                fontSize: 14,
                color: isActiveParent ? "#0A56AF" : "#7E7E7E",
              }}
            >
              {label}
            </Typography>
          )}
        </div>

        {windowWidth >= 1025 && (
          <ChevronRight
            height="20px"
            stroke={isActiveParent ? "#0A56AF" : "#7E7E7E"}
            width="20px"
            style={{
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "0.2s ease",
            }}
          />
        )}
      </DropDownHeader>

      <DropDownContent open={open}>
        {dropItems.map((item, i) => (
          <a
            key={i}
            href={item.path}
            onClick={(e) => {
              e.preventDefault();
              navigate(item.path);
            }}
            style={{ textDecoration: "none" }}
          >
            <MenuItem
              isActive={currentPathname === item.path}
              windowIsSmall={windowWidth < 1024}
              data-tooltip-id={item.label}
              data-tooltip-content={item.label}
              style={{ paddingLeft: windowWidth >= 1025 ? 15 : 0 }}
            >
              {windowWidth < 1024 && <Tooltip id={item.label} />}

              <item.icon
                height="20px"
                width="20px"
                fill={currentPathname === item.path ? "#0A56AF" : "#7E7E7E"}
              />

              {windowWidth >= 1025 && (
                <Typography
                  fontFamily="urbanist"
                  fontWeight="medium"
                  style={{
                    fontSize: 14,
                    color:
                      currentPathname === item.path ? "#0A56AF" : "#7E7E7E",
                  }}
                >
                  {item.label}
                </Typography>
              )}
            </MenuItem>
          </a>
        ))}
      </DropDownContent>
    </DropDownContainer>
  );
};

const NormalItem = ({
  icon,
  label,
  path,
  currentPathname,
  windowWidth,
}: {
  label: string;
  path: string;
  icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  currentPathname: string;
  windowWidth: number;
}) => {
  const navigate = useNavigate();
  const MenuIcon = icon;
  return (
    <a href={path} style={{ textDecoration: "none" }}>
      <MenuItem
        isActive={currentPathname === path}
        windowIsSmall={windowWidth < 1024}
        onClick={(e) => {
          // Prevent the default anchor tag navigation
          e.preventDefault();
          navigate(path);
        }}
        data-tooltip-id={label}
        data-tooltip-content={label}
      >
        {windowWidth < 1024 && <Tooltip id={label}></Tooltip>}
        <MenuIcon
          fill={currentPathname === path ? "#0A56AF" : "#7E7E7E"}
          height={16}
          width={16}
        />
        {windowWidth >= 1025 && (
          <Typography
            fontFamily="urbanist"
            fontWeight="medium"
            style={{
              fontSize: 14,
              color: currentPathname === path ? "#0A56AF" : "#7E7E7E",
            }}
          >
            {label}
          </Typography>
        )}
      </MenuItem>
    </a>
  );
};

export const SideBarMenuItem = (
  props: ContentType & { currentPathname: string; windowWidth: number }
) => {
  if (props.type === "dropType") {
    return (
      <DropDownCollapsible
        {...props}
        currentPathname={props.currentPathname}
        windowWidth={props.windowWidth}
      />
    );
  } else {
    return (
      <NormalItem
        {...props}
        currentPathname={props.currentPathname}
        windowWidth={props.windowWidth}
      />
    );
  }
};
